import { ScholarshipSessionDto } from '@cradua/data';
import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as ScholarshipSessionActions from './scholarshipsession.actions';

export const SCHOLARSHIP_SESSION_FEATURE_KEY = 'scholarshipsession';

export interface ScholarshipSessionState
  extends EntityState<ScholarshipSessionDto> {
  selectedScholarshipSessionId?: string | number; // which ScholarshipSession record has been selected
  loaded: boolean; // has the ScholarshipSession list been loaded
  error?: string | null; // last none error (if any)
}

export interface ScholarshipSessionPartialState {
  readonly [SCHOLARSHIP_SESSION_FEATURE_KEY]: ScholarshipSessionState;
}

export const scholarshipSessionAdapter: EntityAdapter<ScholarshipSessionDto> = createEntityAdapter<
  ScholarshipSessionDto
>({
  selectId: (scholarshipSession) => scholarshipSession.id,
  sortComparer: false,
});

export const initialState: ScholarshipSessionState = scholarshipSessionAdapter.getInitialState(
  {
    // set initial required properties
    loaded: false,
    error: null,
    selectedScholarshipSessionId: null,
  }
);

const scholarshipSessionReducer = createReducer(
  initialState,
  on(ScholarshipSessionActions.loadScholarshipSession, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ScholarshipSessionActions.loadScholarshipSessionSuccess,
    (state, { scholarshipSession }) =>
      scholarshipSessionAdapter.addAll(scholarshipSession, {
        ...state,
        loaded: false,
      })
  ),
  on(
    ScholarshipSessionActions.loadScholarshipSessionFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(ScholarshipSessionActions.addScholarshipSession, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ScholarshipSessionActions.addScholarshipSessionSuccess,
    (state, { newScholarshipSession }) =>
      scholarshipSessionAdapter.addMany(newScholarshipSession, {
        ...state,
        loaded: false,
      })
  ),
  on(
    ScholarshipSessionActions.addScholarshipSessionFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(ScholarshipSessionActions.updateScholarshipSession, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ScholarshipSessionActions.updateScholarshipSessionSuccess,
    (state, { updateScholarshipSession }) =>
      scholarshipSessionAdapter.upsertMany(updateScholarshipSession, {
        ...state,
        loaded: false,
      })
  ),
  on(
    ScholarshipSessionActions.updateScholarshipSessionFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(ScholarshipSessionActions.removeScholarshipSession, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    ScholarshipSessionActions.removeScholarshipSessionSuccess,
    (state, { ids }) =>
      scholarshipSessionAdapter.removeMany(ids, { ...state, loaded: false })
  ),
  on(
    ScholarshipSessionActions.removeScholarshipSessionFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  )
);

export function reducer(
  state: ScholarshipSessionState | undefined,
  action: Action
) {
  return scholarshipSessionReducer(state, action);
}
